import * as React from 'react';
import styled from 'styled-components';
import {CourseObject, TeacherObject} from 'api/course';
import {useFormikContext} from 'formik';
import Row from 'components/Row';
import Column from 'components/Column';
import Input, {InputVariant} from 'components/Input';
import Button, {ButtonVariant} from 'components/Button';
import FormContainer from '../../containers/FormContainer';
import TodoContainer from '../containers/TodoContainer';
import PreviewPicker, {
  Container as PreviewContainer,
} from 'components/PreviewPicker';
import {PreviewObject} from '../../../../../types';

const Container = styled.div`
  gap: 20px;
  grid-template-columns: 180px 1fr;
  display: grid;
  
  ${PreviewContainer} {
    grid-row: span 3;
    height: 100%;
  }
`;

/**
 * The authorMessage form part of landing
 * @return {React.ReactElement}
 * @constructor
 */
export default function AuthorMessageForm(): React.ReactElement {
  const formContext = useFormikContext<Partial<CourseObject>>();

  /**
   * Render content
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
    /**
     * Render the teacher.
     * @return {JSX.Element}
     */
    function renderTeacher(): JSX.Element {
      /**
       * Render preview.
       * @return {JSX.Element}
       */
      function renderPreview(): JSX.Element {
        function handleChange(preview: PreviewObject): void {
          formContext.setFieldValue(`authorMessage.teacher.preview`, preview);
        }

        return (
          <PreviewPicker
            preview={formContext.values.authorMessage?.teacher?.preview}
            onChange={handleChange}
          />
        );
      }

      /**
       * Render text.
       * @return {JSX.Element}
       */
      function renderText(): JSX.Element {
        /**
         * This blur event handler.
         * @param {React.FocusEvent<HTMLInputElement>} event
         */
        function handleBlur(
            event: React.FocusEvent<HTMLInputElement>,
        ): void {
          formContext.setFieldValue(
              `authorMessage.teacher.text`,
              event.target.value,
          );
          formContext.submitForm();
        }

        return (
          <Row>
            <Column>
              <Input
                type="text"
                value={formContext.values.authorMessage?.teacher ? (
                  formContext.values.authorMessage?.teacher.text
                ) : undefined}
                label="Текст"
                onChange={handleBlur}
                placeholder="Текст"
                required
              />
            </Column>
          </Row>
        );
      }

      /**
       * Render name.
       * @return {JSX.Element}
       */
      function renderName(): JSX.Element {
        /**
         * This blur event handler.
         * @param {React.FocusEvent<HTMLInputElement>} event
         */
        function handleBlur(
            event: React.FocusEvent<HTMLInputElement>,
        ): void {
          formContext.setFieldValue(
              `authorMessage.teacher.name`,
              event.target.value,
          );
          formContext.submitForm();
        }

        return (
          <Row>
            <Column>
              <Input
                type="text"
                value={formContext.values.authorMessage?.teacher ? (
                  formContext.values.authorMessage?.teacher.name
                ) : undefined}
                label="Имя"
                onChange={handleBlur}
                placeholder="Имя"
                required
              />
            </Column>
          </Row>
        );
      }

      /**
       * Render answer.
       * @return {JSX.Element}
       */
      function renderRole(): JSX.Element {
        /**
         * This blur event handler.
         * @param {React.FocusEvent<HTMLInputElement>} event
         */
        function handleBlur(
            event: React.FocusEvent<HTMLInputElement>,
        ): void {
          formContext.setFieldValue(
              `authorMessage.teacher.role`,
              event.target.value,
          );
          formContext.submitForm();
        }

        return (
          <Row>
            <Column>
              <Input
                type="text"
                value={formContext.values.authorMessage?.teacher ? (
                  formContext.values.authorMessage?.teacher.role
                ) : undefined}
                label="Описание"
                onChange={handleBlur}
                placeholder="Описание"
                required
              />
            </Column>
          </Row>
        );
      }

      return (
        <Container>
          {renderPreview()}
          {renderText()}
          {renderName()}
          {renderRole()}
        </Container>
      );
    }

    return (
      <Row>
        <Column>
          {renderTeacher()}
        </Column>
      </Row>
    );
  }
  return (
    <FormContainer
      property="authorMessage"
      label="Слово автора/преподавателя курса"
      collapse
    >
      {renderContent()}
    </FormContainer>
  );
}
